import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import ptBrLocale from 'date-fns/locale/pt-BR';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import getValidationErrors from '../../../utils/getValidationErrors';
import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import mask from '../../../utils/masks';
import validaCpf from '../../../utils/validaCpf';
import validaCnpj from '../../../utils/validaCnpj';
import ICliente from '../../../interfaces/cliente';
import { IBuscaCep, IConfiguracaoAdmin } from './helpers/types';
import { criarSchema } from './helpers/validation';
import { diaDaSemanaParaNumero } from './helpers/helpers';
import { STRINGS } from './helpers/strings';

export function useClienteCadastro(id: string | undefined) {
  const { user, company, companies } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();

  const formRef = useRef<FormHandles>(null);
  const mounted = useRef(false);

  const [cpf_cnpj_proibido, setCpfCnpjProibido] = useState('');
  const [isJuridica, setIsJuridica] = useState<boolean>(false);
  const [cod_retorno_integracao, setCodRetornoIntegracao] = useState(0);
  const [motivo_integracao, setMotivoIntegracao] = useState('');
  const [prazos, setPrazos] = useState([]);
  const [data_inicio, setDataInicio] = useState<number | null>(null);
  const [data_inicio_agenda, setDataInicioAgenda] = useState<Date | null>(null);
  const [grupo_clientes, setGrupoClientes] = useState([]);
  const [formas_pagamento, setFormasPagamento] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState('');
  const [tabela_precos, setTabelaPrecos] = useState([]);
  const [checked, setChecked] = useState(false);
  const [bloquear_alteracao_prazo, setBloquearAlteracaoPrazo] = useState(false);
  const [bloquear_alteracao_forma_pagamento, setBloquearAlteracaoFormaPagamento] = useState(false);
  const [bloquear_alteracao_grupo_clientes, setBloquearAlteracaoGrupoClientes] = useState(false);
  const [bloquear_alteracao_tabela_preco, setBloquearAlteracaoTabelaPreco] = useState(false);
  const [prazo_id, setPrazoId] = useState<number | null>(null);
  const [forma_pagamento_id, setFormaPagamentoId] = useState<number | null>(null);
  const [grupo_clientes_id, setGrupoClientesId] = useState<number | null>(null);
  const [tabela_preco_id, setTabelaPrecoId] = useState<number | null>(null);

  const role = companies && company && id ? companies[company.id].role : '';

  const data_texto = useMemo(() => {
    if (!data_inicio_agenda) return '';
    return format(new Date(data_inicio_agenda), 'EEEE', { locale: ptBrLocale });
  }, [data_inicio_agenda]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    api
      .get<IConfiguracaoAdmin>('busca-configuracoes-empresa')
      .then(retorno => {
        setBloquearAlteracaoPrazo(retorno.data.bloquear_alteracao_prazo);
        setBloquearAlteracaoFormaPagamento(retorno.data.bloquear_alteracao_forma_pagamento);
        setBloquearAlteracaoGrupoClientes(retorno.data.bloquear_alteracao_grupo_clientes);
        setBloquearAlteracaoTabelaPreco(retorno.data.bloquear_alteracao_tabela_preco);
      })
      .catch(_err => undefined);
  }, [id]);

  useEffect(() => {
    async function getCliente(): Promise<void> {
      if (!id) return;
      try {
        setIsLoading(true);
        const { data } = await api.get<ICliente>(`cliente/${id}`);

        let juridica = false;
        if (data.pessoa.juridica) {
          juridica = data.pessoa.juridica;
        } else {
          juridica = !!data.pessoa.cpf_cnpj && data.pessoa.cpf_cnpj.length > 11;
        }

        setIsJuridica(juridica);
        setCodRetornoIntegracao(data.cod_retorno_integracao);
        setMotivoIntegracao(data.motivo_integracao);
        setChecked(data.pessoa.rg_insc === 'ISENTO');
        setDataInicioAgenda(data.data_inicio_agenda ? new Date(data.data_inicio_agenda) : null);
        setDataInicio(data.periodicidade ?? null);
        setPrazoId(data.prazo_id);
        setFormaPagamentoId(data.forma_pagamento_id);
        setGrupoClientesId(data.grupo_cliente_id ?? null);
        setTabelaPrecoId(data.tabela_preco_id ?? null);

        formRef.current?.setData({
          grupo_cliente_id: data.grupo_cliente_id,
          tabela_preco_id: data.tabela_preco_id,
          prazo_id: data.prazo_id,
          forma_pagamento_id: data.forma_pagamento_id,
          vendedor_id: data.vendedor_id,
          ativo: data.ativo,
          periodicidade: data.periodicidade,
          dia_atendimento: data.dia_atendimento,
          data_inicio_agenda: data.data_inicio_agenda,
          observacao: data.observacao,
          observacao_entrega: data.observacao_entrega,
          pessoa: {
            juridica,
            nome: data.pessoa.nome,
            apelido: data.pessoa.apelido,
            cpf_cnpj: mask({
              input: data.pessoa.cpf_cnpj,
              tipo: juridica ? 'cnpj' : 'cpf',
            }),
            rg_insc: data.pessoa.rg_insc,
            endereco: {
              cep: mask({ input: data.pessoa.endereco?.cep, tipo: 'cep' }),
              logradouro: data.pessoa.endereco?.logradouro,
              numero: data.pessoa.endereco?.numero,
              complemento: data.pessoa.endereco?.complemento,
              bairro: data.pessoa.endereco?.bairro,
              cidade: data.pessoa.endereco?.cidade,
              estado: data.pessoa.endereco?.estado,
            },
            contato: {
              telefone: mask({ input: data.pessoa.contato?.telefone, tipo: 'fone' }),
              celular: mask({ input: data.pessoa.contato?.celular, tipo: 'fone' }),
              email: data.pessoa.contato?.email,
              outros: data.pessoa.contato?.outros,
            },
          },
        });
      } catch (err: any) {
        addToast({
          ...STRINGS.toast.erroCarregar,
          description: err?.response?.data?.message ?? STRINGS.toast.erroCarregar.description,
        });
      } finally {
        setIsLoading(false);
      }
    }

    Promise.all([
      api.get('forma-pagamento'),
      api.get('prazo'),
      api.get('grupo-cliente'),
      api.get('colaborador'),
      api.get('tabela-preco'),
    ])
      .then(async ([
        retorno_formas_pagamento,
        retorno_prazos,
        retorno_grupo_clientes,
        retorno_vendedores,
        retorno_tabela_precos,
      ]) => {
        setFormasPagamento(retorno_formas_pagamento.data);
        setPrazos(retorno_prazos.data);
        setGrupoClientes(retorno_grupo_clientes.data);
        setVendedores(
          retorno_vendedores.data.map((vendedor: any) => ({
            value: vendedor.id,
            label: vendedor?.pessoa?.nome,
          })),
        );
        setTabelaPrecos(retorno_tabela_precos.data);
        await getCliente();
      })
      .catch(_err => undefined);
  }, [id, addToast]);

  const buscaCepViacep = useCallback(async (cep: string) => {
    try {
      const cepPesquisar = cep.replace(/\D+/g, '');
      if (cepPesquisar.length === 8) {
        const { data } = await axios.get(
          `https://viacep.com.br/ws/${cepPesquisar}/json/`,
        );
        formRef.current?.setFieldValue('pessoa.endereco.estado', data.uf);
        formRef.current?.setFieldValue('pessoa.endereco.cidade', data.localidade);
        formRef.current?.setFieldValue('pessoa.endereco.logradouro', data.logradouro);
        formRef.current?.setFieldValue('pessoa.endereco.bairro', data.bairro);
        formRef.current?.setFieldValue('pessoa.endereco.codigo_ibge', data.ibge);
        formRef.current?.setFieldValue('pessoa.endereco.complemento', data.complemento);
      }
    } catch (_err) {
      // erro de CEP ignorado intencionalmente
    }
  }, []);

  const pesquisaEnderecoViacep = useCallback((endereco: IBuscaCep) => {
    formRef.current?.setFieldValue('pessoa.endereco.cep', endereco.cep);
    formRef.current?.setFieldValue('pessoa.endereco.logradouro', endereco.logradouro);
    formRef.current?.setFieldValue('pessoa.endereco.complemento', endereco.complemento);
    formRef.current?.setFieldValue('pessoa.endereco.bairro', endereco.bairro);
    formRef.current?.setFieldValue('pessoa.endereco.cidade', endereco.cidade);
    formRef.current?.setFieldValue('pessoa.endereco.estado', endereco.estado);
    formRef.current?.setFieldValue('pessoa.endereco.numero', '');
  }, []);

  async function handleFindCnpjReceita(cnpj: string): Promise<void> {
    if (id || !isJuridica || cnpj.length !== 14) return;
    try {
      setIsLoading(true);
      const response = await api.post(`buscar-empresa-por-cnpj?cnpj=${cnpj}`);
      if (response.data) {
        const { data } = response;
        formRef.current?.setData({
          pessoa: {
            cpf_cnpj: mask({ input: cnpj, tipo: 'cnpj' }),
            juridica: true,
            nome: data.razao_social,
            apelido: data.nome_fantasia,
            endereco: {
              cep: data.cep,
              logradouro: data.logradouro,
              numero: data.numero,
              complemento: data.complemento,
              bairro: data.bairro,
              cidade: data.cidade,
              estado: data.estado,
            },
            contato: {
              telefone: data.telefone,
              email: data.email,
            },
          },
        });
      }
    } catch (_err) {
      // erro ao buscar CNPJ na Receita ignorado intencionalmente
    } finally {
      setIsLoading(false);
    }
  }

  function handleMessage(cliente_id: number, nome: string): void {
    confirmAlert({
      title: STRINGS.confirmacoes.atencao,
      message: STRINGS.confirmacoes.cpfCnpjExistente(nome),
      buttons: [
        {
          label: STRINGS.confirmacoes.carregar,
          onClick: () => history.push(`/clientes/cadastro/${cliente_id}`),
        },
        {
          label: STRINGS.confirmacoes.cancelar,
          onClick: () => formRef.current?.setFieldValue('pessoa.cpf_cnpj', ''),
        },
      ],
      onClickOutside: () => formRef.current?.setFieldValue('pessoa.cpf_cnpj', ''),
      closeOnEscape: false,
    });
  }

  function handleErrorMessage(mensagem: string): void {
    confirmAlert({
      title: STRINGS.confirmacoes.atencao,
      message: mensagem,
      buttons: [{ label: STRINGS.confirmacoes.sair, onClick: () => undefined }],
    });
  }

  async function findCpfCnpj(input: string): Promise<void> {
    const cpf_cnpj = input.toUpperCase().replace(/[^A-Z0-9]/g, '');

    if (!cpf_cnpj) return;

    const valido = isJuridica ? validaCnpj(cpf_cnpj) : validaCpf(cpf_cnpj);
    if (!valido) {
      formRef.current?.setErrors({
        'pessoa.cpf_cnpj': isJuridica ? 'Cnpj inválido' : 'Cpf inválido',
      });
      return;
    }

    formRef.current?.setErrors({ 'pessoa.cpf_cnpj': '' });

    try {
      setIsLoading(true);
      const response = await api.get(`/cliente/findByCpfCnpj/${cpf_cnpj}`);
      setCpfCnpjProibido(cpf_cnpj);
      if (
        `${id}` !== `${response.data.id}` &&
        (response.data.empresa_id === company.id || !response.data.empresa_id) &&
        (response.data.vendedor_id === user.colaborador_id || !response.data.vendedor_id)
      ) {
        handleMessage(response.data.id, response.data.pessoa.nome);
      } else if (
        `${id}` !== `${response.data.id}` &&
        response.data.empresa_id &&
        response.data.empresa_id !== company.id
      ) {
        formRef.current?.setFieldValue('pessoa.cpf_cnpj', '');
        handleErrorMessage(STRINGS.confirmacoes.cpfCnpjOutraEmpresa);
      } else if (
        `${id}` !== `${response.data.id}` &&
        response.data.vendedor_id &&
        response.data.vendedor_id !== user.colaborador_id
      ) {
        formRef.current?.setFieldValue('pessoa.cpf_cnpj', '');
        handleErrorMessage(STRINGS.confirmacoes.cpfCnpjOutroVendedor);
      } else {
        setCpfCnpjProibido('');
      }
    } catch (err) {
      setCpfCnpjProibido('');
      await handleFindCnpjReceita(cpf_cnpj);
    } finally {
      setIsLoading(false);
    }
  }

  function handleIsento(): void {
    setChecked(!checked);
    formRef.current?.setFieldValue('pessoa.rg_insc', !checked ? 'ISENTO' : '');
  }

  const handleSubmit = useCallback(
    async (data: ICliente) => {
      let new_data = { ...data };

      if (data_inicio_agenda !== null) {
        const data_formated = format(
          new Date(data_inicio_agenda),
          'yyyy-MM-dd HH:mm:ss',
        );
        new_data = {
          ...data,
          data_inicio_agenda: data_formated,
          dia_atendimento: diaDaSemanaParaNumero(data_texto),
        };
      }

      try {
        setLoadingMessage(STRINGS.salvar);
        formRef.current?.setErrors({});

        await criarSchema(cpf_cnpj_proibido).validate(data, { abortEarly: false });

        const retorno = id
          ? await api.put(`/cliente/${id}`, new_data)
          : await api.post('/cliente', new_data);

        if (mounted.current) {
          setCodRetornoIntegracao(retorno.data.cod_retorno_integracao);
          setMotivoIntegracao(retorno.data.motivo_integracao);
          if (retorno.data.cod_retorno_integracao < 400) {
            addToast(STRINGS.toast.sucesso);
            history.push('/clientes/lista');
          } else {
            addToast(STRINGS.toast.erroSync);
            history.push(`/clientes/cadastro/${retorno.data.id}`);
          }
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }
        addToast({
          ...STRINGS.toast.erroCadastro,
          description: (err as any)?.response?.data?.message,
        });
      } finally {
        setLoadingMessage('');
      }
    },
    [addToast, history, id, cpf_cnpj_proibido, data_inicio_agenda, data_texto],
  );

  return {
    formRef,
    isLoading,
    loading_message,
    isJuridica,
    setIsJuridica,
    checked,
    data_inicio,
    setDataInicio,
    data_inicio_agenda,
    setDataInicioAgenda,
    data_texto,
    cod_retorno_integracao,
    motivo_integracao,
    prazos,
    formas_pagamento,
    grupo_clientes,
    vendedores,
    tabela_precos,
    bloquear_alteracao_prazo,
    bloquear_alteracao_forma_pagamento,
    bloquear_alteracao_grupo_clientes,
    bloquear_alteracao_tabela_preco,
    prazo_id,
    forma_pagamento_id,
    grupo_clientes_id,
    tabela_preco_id,
    role,
    handleSubmit,
    buscaCepViacep,
    pesquisaEnderecoViacep,
    findCpfCnpj,
    handleIsento,
  };
}
